import { z } from 'zod';
import { CHAT_MAX_LENGTH, EMOTES } from './chat';

export const usernameSchema = z
  .string()
  .transform((s) => s.normalize('NFC').trim().replace(/\s+/g, ' '))
  .pipe(z.string().regex(/^[\p{L}\p{N} _-]{2,20}$/u, 'Nombre inválido'));

const resourceSchema = z.enum(['wood', 'stone', 'clay', 'ore', 'glass', 'papyrus', 'textile']);
const countSchema = z.partialRecord(resourceSchema, z.number().int().min(0).max(7));
const cardIid = z.string().min(3).max(60);
const paymentSchema = z.object({ buy: z.object({ left: countSchema, right: countSchema }) });

export const selectionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('build'), card: cardIid, payment: paymentSchema }),
  z.object({ action: z.literal('wonder'), card: cardIid, payment: paymentSchema }),
  z.object({ action: z.literal('discard'), card: cardIid }),
]);

const wonderId = z.enum(['alexandria', 'babylon', 'ephesos', 'gizah', 'halikarnassos', 'olympia', 'rhodos']);
const decisionId = z.number().int().min(0);

export const roomSettingsSchema = z.object({
  wonderMode: z.enum(['random', 'choose']),
  sidePolicy: z.enum(['choose', 'day', 'night', 'random']),
  seating: z.enum(['random', 'lobbyOrder']),
  visibility: z.enum(['public', 'private']),
});

export const roomCodeSchema = z
  .string()
  .transform((s) => s.trim().toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9]{4,8}$/));

/** Esquemas de los payloads cliente→servidor (los eventos sin payload aceptan `undefined`). */
export const clientSchemas = {
  'lobby:subscribe': z.undefined().or(z.object({})),
  'lobby:unsubscribe': z.undefined().or(z.object({})),
  'room:create': z.object({ settings: roomSettingsSchema.partial().optional() }),
  'room:join': z.object({ code: roomCodeSchema }),
  'room:leave': z.undefined().or(z.object({})),
  'room:updateSettings': z.object({ settings: roomSettingsSchema.partial() }),
  'room:pickWonder': z.object({ wonder: wonderId.nullable() }),
  'room:kick': z.object({ userId: z.string().min(1).max(64) }),
  'room:transferHost': z.object({ userId: z.string().min(1).max(64) }),
  'room:start': z.undefined().or(z.object({})),
  'room:backToLobby': z.undefined().or(z.object({})),
  'game:chooseSide': z.object({ decisionId, side: z.enum(['day', 'night']) }),
  'game:select': z.object({ decisionId, selection: selectionSchema }),
  'game:unselect': z.object({ decisionId }),
  'game:buildFromDiscard': z.object({ decisionId, card: cardIid }),
  'game:playLastCard': z.object({ decisionId, selection: selectionSchema }),
  'game:abandon': z.undefined().or(z.object({})),
  'game:retire': z.object({ userId: z.string().min(1).max(64) }),
  'chat:send': z.object({ text: z.string().trim().min(1).max(CHAT_MAX_LENGTH) }),
  'chat:emote': z.object({ emote: z.enum(EMOTES) }),
  'sync:request': z.undefined().or(z.object({})),
} as const;

export type ClientEventName = keyof typeof clientSchemas;
export type ClientPayload<E extends ClientEventName> = z.output<(typeof clientSchemas)[E]>;
export type ClientInput<E extends ClientEventName> = z.input<(typeof clientSchemas)[E]>;

export const sessionRequestSchema = z.object({ username: usernameSchema, takeover: z.boolean().optional() });
